import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  useGetVersionsQuery,
  useAllCommentsGenericQuery,
  useLogEntriesByReferenceQuery,
  LogCategory,
  ShiftType,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { selectedDate as selectedDateAtom, selectedWorkpackIdForKpiTracker } from '../state';
import { formatDateToYYYYMMDD } from '../utils';
import { COLLECTION_NAMES } from '@/constants';
import { KPI_NOTE_LOG_METADATA_KEY } from '../constants';

const WORKPACK_COLLECTION_NAME = 'Workpack';
const LOG_ENTRY_COLLECTION_NAME = 'LogEntry';

export const useKpiHistoryData = () => {
  const structureId = useGetStructureId();
  const workpackId = useAtomValue(selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(selectedDateAtom);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatDateToYYYYMMDD(selectedDate);

  const { data: worklogLogEntriesData, loading: worklogLogEntriesLoading } =
    useLogEntriesByReferenceQuery({
      variables: {
        input: {
          referenceId: workpackId ?? '',
          structureId: structureId ?? '',
          category: LogCategory.Worklog,
          logDate: formattedDate,
        },
      },
      skip: !structureId || !workpackId,
      fetchPolicy: 'cache-and-network',
    });

  /** No `shift` filter: history lists all job-phase / QA-QC log entries for `logDate` (Day + Night). */
  const { data: qaqcLogEntriesData, loading: qaqcLogEntriesLoading } =
    useLogEntriesByReferenceQuery({
      variables: {
        input: {
          referenceId: workpackId ?? '',
          structureId: structureId ?? '',
          category: LogCategory.QaqcForm,
          logDate: formattedDate,
        },
      },
      skip: !structureId || !workpackId,
      fetchPolicy: 'cache-and-network',
    });

  const logEntryIds = useMemo(() => {
    const worklogIds = worklogLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? [];
    const qaqcIds = qaqcLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? [];
    return [...new Set([...worklogIds, ...qaqcIds])];
  }, [worklogLogEntriesData?.logEntriesByReference, qaqcLogEntriesData?.logEntriesByReference]);

  const qaqcLogEntryIdSet = useMemo(
    () => new Set(qaqcLogEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? []),
    [qaqcLogEntriesData?.logEntriesByReference]
  );

  /** LogEntry versions use `referenceId` as the log entry id; shift lives on the entry, not in version payload. */
  const logEntryShiftById = useMemo(() => {
    const map: Record<string, ShiftType | null | undefined> = {};
    for (const entry of worklogLogEntriesData?.logEntriesByReference ?? []) {
      map[entry.id] = entry.shift;
    }
    for (const entry of qaqcLogEntriesData?.logEntriesByReference ?? []) {
      map[entry.id] = entry.shift;
    }
    return map;
  }, [worklogLogEntriesData?.logEntriesByReference, qaqcLogEntriesData?.logEntriesByReference]);

  const {
    data: workpackVersionsData,
    loading: workpackLoading,
    error: workpackError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName: WORKPACK_COLLECTION_NAME,
        referenceId: workpackId,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip: !structureId || !workpackId,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: logEntryVersionsData,
    loading: logEntryVersionsLoading,
    error: logEntryError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName: LOG_ENTRY_COLLECTION_NAME,
        referenceIds: logEntryIds,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip: !structureId || !workpackId || logEntryIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const { data: commentsData, loading: commentsLoading } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName: COLLECTION_NAMES.WORKPACK,
        referenceId: workpackId as string,
        structureId,
        metadata: {
          [KPI_NOTE_LOG_METADATA_KEY]: formattedDate,
        },
      },
    },
    skip: !structureId || !workpackId,
    fetchPolicy: 'cache-and-network',
  });

  const loading =
    workpackLoading ||
    logEntryVersionsLoading ||
    commentsLoading ||
    worklogLogEntriesLoading ||
    qaqcLogEntriesLoading;
  const error = workpackError || logEntryError;

  return {
    workpackVersionsData,
    logEntryVersionsData,
    commentsData,
    logEntryIds,
    logEntryShiftById,
    qaqcLogEntryIdSet,
    loading,
    error,
  };
};
